import { entityScripts } from "@/lib/entityJsonLd";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import LeagueHub from "@/pages/LeagueHub";
import { MainLayout } from "@/components/layout/MainLayout";
import { socialMeta, readable } from "@/lib/routeSocial";
import { findLeagueCluster } from "@/lib/seoClusters";
import { getEntityPicks } from "@/lib/publicPicks.functions";
import { ServerPicksList, picksJsonLd } from "@/components/predictions/ServerPicksList";

// Old slugs → the readable canonical slug Google should rank.
const LEAGUE_ALIASES: Record<string, string> = {
  epl: "premier-league",
  "english-premier-league": "premier-league",
  ucl: "champions-league",
  "uefa-champions-league": "champions-league",
  laliga: "la-liga",
  uel: "europa-league",
  "efl-championship": "championship",
};
/** Canonical URL slug → internal cluster slug. */
const CLUSTER_OF: Record<string, string> = {};

const leagueQuery = (slug: string) => {
  const cluster = findLeagueCluster(CLUSTER_OF[slug] ?? slug);
  const terms = cluster?.matchers?.length ? cluster.matchers.slice(0, 6) : [readable(slug)];
  return queryOptions({ queryKey: ["entity-picks", "league", slug], queryFn: () => getEntityPicks({ data: { field: "league", terms } }), staleTime: 2 * 60_000 });
};

export const Route = createFileRoute("/leagues/$slug")({
  beforeLoad: ({ params }) => {
    const target = LEAGUE_ALIASES[params.slug];
    if (target) throw redirect({ to: "/leagues/$slug", params: { slug: target }, statusCode: 301 });
  },
  loader: ({ context, params }) => context.queryClient.ensureQueryData(leagueQuery(params.slug)),
  head: ({ params, loaderData }) => {
    const cluster = findLeagueCluster(CLUSTER_OF[params.slug] ?? params.slug);
    const label = cluster?.label ?? `${readable(params.slug)} Predictions`;
    return {
      scripts: [
        ...entityScripts({ type: "SportsOrganization", name: label, path: `/leagues/${params.slug}`, crumbs: [{ name: "Leagues", path: "/leagues" }, { name: label, path: `/leagues/${params.slug}` }], extra: { sport: "Association football" } }),
        ...(loaderData && loaderData.length ? [{ type: "application/ld+json", children: picksJsonLd(loaderData) }] : []),
      ],
      meta: socialMeta({
        seo: { topics: [params.slug, "leagues", "footballPredictions"] },
        title: cluster?.title ?? `${label} & Betting Tips | Statlign`,
        description:
          cluster?.description ??
          `${label}, value odds and betting tips from the Statlign 50-strategy engine — every pick shows its reasoning.`,
      }),
    };
  },
  component: LeagueRoute,
  errorComponent: () => <MainLayout><LeagueHub /></MainLayout>,
  notFoundComponent: () => <MainLayout><LeagueHub /></MainLayout>,
});

function LeagueRoute() {
  const { slug } = Route.useParams();
  const { data } = useSuspenseQuery(leagueQuery(slug));
  return (
    <MainLayout>
      <LeagueHub />
      <div className="container mx-auto px-4"><ServerPicksList picks={data} /></div>
    </MainLayout>
  );
}
